// PWR-02 (headroom) — README.md. Not grouped ("optional; correctness
// first") — one issue per speaker.

import type { BuildContext } from '../context';
import type { Issue, RuleId } from '../types';
import { CHANNEL_NAME, fmtDb, fmtWatts, roundUpTenWatts, issueId } from '../copy';

const TARGET_SPL = 95; // THX 105 dB peak - 10 dB headroom
const ROOM_GAIN_DB = 3;

export function checkPwr02(ctx: BuildContext): { issues: Issue[]; unknown: { rule: RuleId; productIds: string[] }[] } {
  if (!ctx.assignment || !ctx.listener) return { issues: [], unknown: [] };
  const listenerPos = ctx.listener.position;
  const issues: Issue[] = [];
  const unknownProductIds = new Set<string>();

  for (const [speakerId, a] of ctx.assignment.bySpeaker) {
    if (a.active || !a.driving || a.driving.kind === 'preout') continue;
    const product = a.product;
    if (!product || product.specs.kind !== 'speaker') continue;

    const sensitivity = product.specs.sensitivityDb;
    let power: { w8: number; w4?: number } | undefined;
    if (a.driving.kind === 'internal') {
      power = ctx.avUnit?.specs.power;
    } else {
      const gearId = a.driving.gearId;
      const found = ctx.amps.find((x) => x.gearId === gearId);
      power = found?.specs.power;
    }

    if (!sensitivity) { unknownProductIds.add(product.id); continue; }
    if (!power) {
      // AV unit / amp missing a power rating — an amp with ampChannels > 0
      // (internal) or any power_amp (external) always carries one per
      // schema, so this is defensive rather than reachable via valid data.
      const ampProductId = a.driving.kind === 'internal' ? ctx.avUnit?.product.id : undefined;
      if (ampProductId) unknownProductIds.add(ampProductId);
      continue;
    }

    const entry = ctx.doc.speakers.find((s) => s.id === speakerId);
    if (!entry) continue;
    const z = product.specs.impedanceOhm?.v;
    if (!z) { unknownProductIds.add(product.id); continue; }

    const refAdjust = (sensitivity.ref === '2.83V' || sensitivity.ref === 'unstated')
      ? 10 * Math.log10(8 / z)
      : 0;
    const S = sensitivity.v - refAdjust - (sensitivity.inRoom ? 3 : 0);

    const dx = entry.position[0] - listenerPos[0];
    const dy = entry.position[1] - listenerPos[1];
    const dz = entry.position[2] - listenerPos[2];
    const d = Math.max(1, Math.hypot(dx, dy, dz));

    const pAvail = (z <= 4.5 && power.w4) ? power.w4 : power.w8;
    const spl = S + 10 * Math.log10(pAvail) - 20 * Math.log10(d) + ROOM_GAIN_DB;
    if (spl >= TARGET_SPL) continue;

    const pNeedRaw = Math.pow(10, (TARGET_SPL - S + 20 * Math.log10(d) - ROOM_GAIN_DB) / 10);
    const pNeed = roundUpTenWatts(pNeedRaw);
    const channel = ctx.channels[speakerId];

    issues.push({
      id: issueId('PWR-02', [speakerId]),
      rule: 'PWR-02',
      severity: 'warning',
      text: `${CHANNEL_NAME[channel]} peaks at ${fmtDb(spl)} dB at your seat.`,
      fix: `Use at least ${fmtWatts(pNeed)} W for this speaker.`,
      subjects: [speakerId],
    });
  }

  const unknown = unknownProductIds.size > 0
    ? [{ rule: 'PWR-02' as RuleId, productIds: [...unknownProductIds].sort() }]
    : [];
  return { issues, unknown };
}
